import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Badge, Button, EditArticleButton, classesDeBotao } from '@/components/atoms';
import { StoryPicker } from '@/components/StoryPicker/StoryPicker';
import { boardApi, type AposentadoriaItem } from '@/services/board';
import { destinationsApi } from '@/services/config';
import { motivoLegivel } from '@/services/erroLegivel';
import { metricsApi, type RundownRow } from '@/services/metrics';
import { usePlatformAccess } from '@/services/platform';
import { useArticles, useCategories } from '@/hooks/useArticles';
import { useBulkAddManifestations } from '@/hooks/useStories';
import { useMarcaAssumida } from '@/store/useMarcaAssumida';
import type { ArticleMetricsLite, ArticleSummary, CategoryInfo } from '@/types/article';
import type { StorySummary } from '@/types/story';
import { formatarNumero, formatarDataHora } from '@/i18n/formato';

import styles from './Articles.module.css';

// Ordenações: 'recentes' usa a lista do WP; as métricas usam o ranking (rundown, 28d).
const SORTS = [
  { v: 'recentes', label: 'Recentes' },
  { v: 'pageviews', label: 'Mais pageviews' },
  { v: 'ctr', label: 'Maior CTR' },
  { v: 'clicks', label: 'Mais cliques' },
  { v: 'position', label: 'Melhor posição' },
  { v: 'tempo', label: 'Mais tempo de leitura' },
] as const;

const fInt = (n?: number | null) =>
  n == null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n));
const fPct = (n?: number | null) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);
const fPos = (n?: number | null) => (n == null ? '—' : n.toFixed(1));
const fSecs = (n?: number | null) =>
  n == null ? '—' : n >= 60 ? `${Math.floor(n / 60)}m${Math.round(n % 60)}s` : `${Math.round(n)}s`;

type ViewMode = 'grid' | 'list';
const VIEW_STORAGE_KEY = 'wiredesk-articles-view';
const PER_PAGE = 20;

const STATUS_OPTIONS = [
  { value: 'publish,draft', label: 'Todos' },
  { value: 'publish', label: 'Publicados' },
  { value: 'draft', label: 'Rascunhos' },
  { value: 'trash', label: 'Lixeira' },
];
const DEFAULT_STATUS = 'publish,draft';

function loadView(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'list' || stored === 'grid' ? stored : 'grid';
  } catch {
    return 'grid';
  }
}

export function Articles() {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  const [view, setView] = useState<ViewMode>(loadView);
  const [searchParams, setSearchParams] = useSearchParams();
  // F-491: o 400 do backend INSTRUI "Informe a marca: ?site=<slug>", mas a tela
  // nascia com a marca só em memória e ignorava o parâmetro — obedecer ao
  // conselho não mudava nada e a sessão da casa ficava presa no 400.
  const [site, setSite] = useState(() => searchParams.get('site') ?? '');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [origem, setOrigem] = useState('');
  const [sort, setSort] = useState<string>('recentes');
  const [janela, setJanela] = useState('28d');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [after, setAfter] = useState('');
  const [before, setBefore] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState<string | null>(null);
  const bulkAddMutation = useBulkAddManifestations();

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      // ignore
    }
  }, [view]);

  // A sessão da casa não tem client_id: sem marca o backend recusa. A marca
  // assumida no Header é o fallback, mas ela não cabe no inicializador do
  // useState — `isStaff` vem de uma sonda assíncrona e ainda é false no
  // primeiro render. Para TENANT isStaff nunca fica true e nada aqui roda: a
  // marca continua vindo do rankSite (1º site do cliente).
  const { isStaff } = usePlatformAccess();
  const marcaAssumida = useMarcaAssumida((s) => s.slug);
  // Adota a marca uma única vez por montagem; a URL e qualquer escolha
  // explícita no seletor têm precedência e queimam esta adoção.
  const marcaResolvida = useRef(false);
  useEffect(() => {
    if (marcaResolvida.current || !isStaff || !marcaAssumida) return;
    marcaResolvida.current = true;
    if (!searchParams.get('site')) setSite(marcaAssumida);
  }, [isStaff, marcaAssumida, searchParams]);

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const { data: sites } = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 5 * 60_000,
  });

  const ranked = sort !== 'recentes';
  const rankSite = site || sites?.[0]?.slug || '';  // B-34 F0: escopo do tenant, sem default global
  // A lista de sites já vem carregada acima para o seletor. Um 404 na listagem
  // de artigos quando ela está vazia não é erro: é uma conta que ainda não
  // conectou o WordPress.
  const semSite = (sites?.length ?? 0) === 0;
  const origemParam =
    origem === 'sistema' ? 'gnc_desk' : origem === 'manual' ? 'externo' : undefined;
  const rankedQ = useQuery({
    queryKey: ['articles-ranked', rankSite, sort, origemParam, janela],
    queryFn: () => metricsApi.rundown(rankSite, { order: sort, origem: origemParam, window: janela }),
    enabled: ranked,
    staleTime: 60_000,
  });

  const { data: categories } = useCategories(site || undefined);
  const { data, isLoading, error } = useArticles({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    status,
    site: site || undefined,
    origem: origem || undefined,
    categories: selectedCategories.length ? selectedCategories : undefined,
    after: after ? `${after}T00:00:00` : undefined,
    before: before ? `${before}T23:59:59` : undefined,
  });

  const changeSite = (slug: string) => {
    marcaResolvida.current = true;
    setSite(slug);
    setPage(1);
    setSelectedCategories([]);
    clearSelection();
    // Sem gravar a escolha na URL o conselho do erro ("Informe a marca:
    // ?site=<slug>") continua mentira e o link copiado abre noutra marca.
    // replace para trocar de marca não empilhar histórico; os demais
    // parâmetros da URL sobrevivem.
    setSearchParams(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (slug) proximos.set('site', slug);
        else proximos.delete('site');
        return proximos;
      },
      { replace: true },
    );
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const categoryMap = useMemo(() => {
    const m = new Map<number, CategoryInfo>();
    categories?.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  const editorialCategories = useMemo(() => {
    if (!categories) return [];
    return categories
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [categories]);

  const resetFilters = () => {
    setSearch('');
    setStatus(DEFAULT_STATUS);
    setSelectedCategories([]);
    setAfter('');
    setBefore('');
    setPage(1);
  };

  const toggleCategory = (id: number) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
    setPage(1);
  };

  const hasFilters =
    Boolean(search) ||
    status !== DEFAULT_STATUS ||
    selectedCategories.length > 0 ||
    Boolean(after) ||
    Boolean(before);

  const selectedArticles = (data?.items ?? []).filter((a: ArticleSummary) =>
    selectedIds.has(a.id),
  );

  async function handleLinkBatch(story: StorySummary) {
    setBulkFeedback(null);
    setPickerOpen(false);
    try {
      const items = selectedArticles.map((a) => ({
        kind: 'wp_post' as const,
        external_id: String(a.id),
        external_url: a.link,
        site: a.site,
        label: a.title.length > 60 ? `${a.title.slice(0, 58)}…` : a.title,
      }));
      const result = await bulkAddMutation.mutateAsync({
        storyId: story.id,
        items,
      });
      const parts: string[] = [];
      if (result.added_count) parts.push(`${result.added_count} ligado(s)`);
      if (result.skipped_count)
        parts.push(`${result.skipped_count} já estava(m) ligado(s)`);
      if (result.error_count) parts.push(`${result.error_count} erro(s)`);
      setBulkFeedback(`${story.title}: ${parts.join(' · ')}`);
      clearSelection();
    } catch (e) {
      setBulkFeedback(`Erro: ${String(e)}`);
    }
  }

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>Artigos</h1>
        <Badge tone="info" size="sm">
          WordPress
        </Badge>
        {data && (
          <span className={styles.totalBadge}>
            {formatarNumero(data.total)} resultados
          </span>
        )}
        <div className={styles.viewToggle} role="group" aria-label="Modo de visualização">
          <button
            className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('grid')}
            aria-pressed={view === 'grid'}
          >
            Cards
          </button>
          <button
            className={`${styles.viewBtn} ${view === 'list' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
          >
            Lista
          </button>
        </div>

        <Link
          to="/artigos/novo"
          className={classesDeBotao('primary', 'sm')}
          style={{ marginLeft: 'auto', textDecoration: 'none' }}
        >
          Novo artigo
        </Link>
      </header>

      <div className={styles.toolbar}>
        <select
          className={styles.siteSelect}
          value={site}
          onChange={(e) => changeSite(e.target.value)}
          aria-label="Filtrar por site WordPress"
          title="WordPress"
        >
          <option value="">Principal (padrão)</option>
          {(sites ?? []).map((s) => (
            <option key={s.slug} value={s.slug}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Buscar título…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.searchInput}
        />
        <select
          className={styles.siteSelect}
          value={sort}
          onChange={(e) => {
            setSort(e.target.value);
            setPage(1);
          }}
          aria-label="Ordenar por"
          title="Ordenar (métricas = ranking 28 dias)"
        >
          {SORTS.map((s) => (
            <option key={s.v} value={s.v}>
              {s.v === 'recentes' ? s.label : `↓ ${s.label}`}
            </option>
          ))}
        </select>
        {ranked && (
          <select
            className={styles.siteSelect}
            value={janela}
            onChange={(e) => {
              setJanela(e.target.value);
              setPage(1);
            }}
            aria-label="Janela de tempo do ranking"
            title="Janela do ranking"
          >
            <option value="7d">7 dias</option>
            <option value="28d">28 dias</option>
            <option value="90d">90 dias</option>
          </select>
        )}
        <button
          className={`${styles.filterBtn} ${hasFilters ? styles.filterBtnActive : ''}`}
          onClick={() => setShowFilters((s) => !s)}
        >
          Filtros
          {selectedCategories.length > 0 && (
            <Badge tone="info" size="xs">
              {selectedCategories.length}
            </Badge>
          )}
        </button>
        {hasFilters && (
          <button className={styles.clearBtn} onClick={resetFilters}>
            Limpar
          </button>
        )}
      </div>

      {showFilters && (
        <div className={styles.filterPanel}>
          <div className={styles.filterCol}>
            <p className={styles.filterLabel}>Status</p>
            <div className={styles.radioGroup}>
              {STATUS_OPTIONS.map((opt) => (
                <label key={opt.value} className={styles.radio}>
                  <input
                    type="radio"
                    name="status"
                    value={opt.value}
                    checked={status === opt.value}
                    onChange={() => {
                      setStatus(opt.value);
                      setPage(1);
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.filterCol}>
            <p className={styles.filterLabel}>Origem</p>
            <div className={styles.radioGroup}>
              {[
                { value: '', label: 'Todos' },
                { value: 'sistema', label: 'Desk' },
                { value: 'manual', label: 'Externo' },
              ].map((opt) => (
                <label key={opt.value || 'all'} className={styles.radio}>
                  <input
                    type="radio"
                    name="origem"
                    value={opt.value}
                    checked={origem === opt.value}
                    onChange={() => {
                      setOrigem(opt.value);
                      setPage(1);
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.filterCol}>
            <p className={styles.filterLabel}>Período</p>
            <div className={styles.dateRow}>
              <input
                type="date"
                value={after}
                onChange={(e) => {
                  setAfter(e.target.value);
                  setPage(1);
                }}
                className={styles.dateInput}
              />
              <span className={styles.dateSep}>→</span>
              <input
                type="date"
                value={before}
                onChange={(e) => {
                  setBefore(e.target.value);
                  setPage(1);
                }}
                className={styles.dateInput}
              />
            </div>
          </div>

          <div className={styles.filterColWide}>
            <p className={styles.filterLabel}>Editoria / Categoria</p>
            <div className={styles.catGrid}>
              {editorialCategories.length === 0 && (
                <span className={styles.muted}>Sem categorias</span>
              )}
              {editorialCategories.map((cat) => (
                <label key={cat.id} className={styles.catChip}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.id)}
                    onChange={() => toggleCategory(cat.id)}
                  />
                  <span>{cat.name}</span>
                  <span className={styles.catCount}>{cat.count}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <SentinelaBanner site={rankSite} />
      <AposentadoriaBanner />

      {selectedIds.size > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionCount}>
            {selectedIds.size} artigo{selectedIds.size === 1 ? '' : 's'} seleccionado
            {selectedIds.size === 1 ? '' : 's'}
          </span>
          <div className={styles.selectionActions}>
            <Button
              size="sm"
              onClick={() => setPickerOpen(true)}
              loading={bulkAddMutation.isPending}
            >
              Ligar a story
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              Limpar seleção
            </Button>
          </div>
        </div>
      )}

      {bulkFeedback && (
        <div className={styles.bulkFeedback}>{bulkFeedback}</div>
      )}

      {ranked && (
        <RankedList rows={rankedQ.data} loading={rankedQ.isLoading} site={site} />
      )}

      {!ranked && isLoading && <p className={styles.muted}>Carregando…</p>}

      {/* Três situações que a tela tratava como UMA — e a única resposta era
          despejar o objeto de erro em texto. Quem abria o produto pela primeira
          vez recebia a tela de Artigos inteira reduzida a
          "Falha: ApiError: 404 Not Found", que não diz o que houve nem o que
          fazer. Medido nesta auditoria, numa conta recém-criada.

          1. Não há site cadastrado  → não é falha, é o começo. A lista de
             artigos mora no WordPress do cliente; sem site não há de onde ler.
             O lugar certo é a tela de cadastro, e o botão leva até lá.
          2. O servidor recusou     → mostra o motivo em português.
          3. Qualquer outra coisa   → mensagem honesta, sem nome de classe de
             JavaScript na cara de quem só queria ver as matérias. */}
      {!ranked && error && semSite && (
        <div className={styles.empty}>
          <p>Nenhum site WordPress conectado ainda.</p>
          <p className={styles.muted}>
            Seus artigos vivem no WordPress. Conecte o site do seu portal e eles
            aparecem aqui — para ler, editar e publicar sem sair daqui.
          </p>
          <Link to="/config/sites" className={styles.emptyCta}>
            Conectar site WordPress
          </Link>
        </div>
      )}
      {!ranked && error && !semSite && (
        <div className={styles.empty}>
          <p>Não foi possível carregar os artigos.</p>
          <p className={styles.muted}>{motivoLegivel(error)}</p>
        </div>
      )}

      {!ranked && data && data.items.length === 0 && (
        <div className={styles.empty}>Sem artigos com estes filtros.</div>
      )}

      {!ranked && data && data.items.length > 0 && view === 'grid' && (
        <ul className={styles.gridList}>
          {data.items.map((article) => {
            const catNames = article.categories
              .map((cid) => categoryMap.get(cid)?.name)
              .filter(Boolean) as string[];
            const checked = selectedIds.has(article.id);
            return (
              <li
                key={article.id}
                className={`${styles.gridCard} ${checked ? styles.gridCardSelected : ''}`}
              >
                <label className={styles.gridSelect}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(article.id)}
                    aria-label="Seleccionar artigo"
                  />
                </label>
                {article.featured_media ? (
                  <img
                    src={article.featured_media.source_url}
                    alt={article.featured_media.alt_text ?? ''}
                    className={styles.gridThumb}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.gridThumbPlaceholder}>sem imagem</div>
                )}
                <div className={styles.gridBody}>
                  <Link
                    to={`/artigos/${article.id}${site ? `?site=${site}` : ''}`}
                    className={styles.gridLink}
                  >
                    {article.title}
                  </Link>
                  {article.excerpt && (
                    <p className={styles.gridExcerpt}>{article.excerpt}</p>
                  )}
                  <div className={styles.gridMeta}>
                    <span>{formatarDataHora(article.published_at)}</span>
                    <StatusBadge status={article.status} />
                    <OrigemBadge origem={article.origem} />
                    <GncDeskBadge on={article.is_gnc_desk} />
                    {catNames.slice(0, 2).map((n) => (
                      <Badge key={n} tone="neutral" size="xs">
                        {n}
                      </Badge>
                    ))}
                    {article.twitter_post_id && (
                      <Badge tone="info" size="xs">
                        tweet
                      </Badge>
                    )}
                  </div>
                  <MetricsChips m={article.metrics} />
                  <div className={styles.gridActions}>
                    <EditArticleButton id={article.id} site={site} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!ranked && data && data.items.length > 0 && view === 'list' && (
        <ul className={styles.list}>
          {data.items.map((article) => {
            const catNames = article.categories
              .map((cid) => categoryMap.get(cid)?.name)
              .filter(Boolean) as string[];
            const checked = selectedIds.has(article.id);
            return (
              <li
                key={article.id}
                className={`${styles.row} ${checked ? styles.rowSelected : ''}`}
              >
                <label className={styles.rowSelect}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(article.id)}
                    aria-label="Seleccionar artigo"
                  />
                </label>
                {article.featured_media ? (
                  <img
                    src={article.featured_media.source_url}
                    alt={article.featured_media.alt_text ?? ''}
                    className={styles.thumb}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.thumbPlaceholder}>sem imagem</div>
                )}
                <div className={styles.body}>
                  <Link
                    to={`/artigos/${article.id}${site ? `?site=${site}` : ''}`}
                    className={styles.rowLink}
                  >
                    {article.title}
                  </Link>
                  <div className={styles.meta}>
                    <span>{article.site}</span>
                    <span>·</span>
                    <span>{formatarDataHora(article.published_at)}</span>
                    <StatusBadge status={article.status} />
                    <OrigemBadge origem={article.origem} />
                    <GncDeskBadge on={article.is_gnc_desk} />
                    {catNames.map((n) => (
                      <Badge key={n} tone="neutral" size="xs">
                        {n}
                      </Badge>
                    ))}
                    {article.twitter_post_id && (
                      <Badge tone="info" size="xs">
                        tweet
                      </Badge>
                    )}
                  </div>
                  <MetricsChips m={article.metrics} />
                  {article.excerpt && <p className={styles.excerpt}>{article.excerpt}</p>}
                </div>
                <div className={styles.rowActions}>
                  <EditArticleButton id={article.id} site={site} />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!ranked && data && totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={styles.pageBtn}
          >
            ← Anterior
          </button>
          <span className={styles.muted}>
            Página {page} de {formatarNumero(totalPages)}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={styles.pageBtn}
          >
            Próxima →
          </button>
        </div>
      )}

      <StoryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleLinkBatch}
      />
    </section>
  );
}

// ─── M9: sentinelas do site (taxonomia + arquivos de editoria) ──────────────
// Vigia o que o WP-admin do cliente faz por fora: editoria que sumiu, editoria
// que inchou (recategorização em massa) e arquivo inacessível — inclusive o
// 301 para MATÉRIA, que é adivinhação de permalink do WordPress e entrega
// conteúdo errado numa URL com autoridade (soft 404). Foi exatamente o que
// ficou dias sem ninguém ver no cliente #1.

function SentinelaBanner({ site }: { site: string }) {
  const [open, setOpen] = useState(false);
  const [varrendo, setVarrendo] = useState(false);
  const q = useQuery({
    queryKey: ['sentinela', site],
    queryFn: () => metricsApi.sentinela(site),
    enabled: Boolean(site),
    staleTime: 10 * 60_000,
  });
  const alarmes = q.data?.alarmes ?? [];
  const altos = alarmes.filter((a) => a.gravidade === 'alta');
  const varrer = async () => {
    setVarrendo(true);
    try {
      await metricsApi.sentinelaVarrer(site);
      await q.refetch();
    } finally {
      setVarrendo(false);
    }
  };
  if (!site || alarmes.length === 0) return null;
  return (
    <div className={styles.sentinelaBanner}>
      <button className={styles.retireHead} onClick={() => setOpen((s) => !s)}>
        <span className={styles.sentinelaFlag}>◈</span>
        <strong>{alarmes.length}</strong>&nbsp;
        {alarmes.length === 1 ? 'alerta de estrutura em' : 'alertas de estrutura em'}
        &nbsp;<strong>{site}</strong>
        {altos.length > 0 && <>&nbsp;({altos.length} grave{altos.length === 1 ? '' : 's'})</>}
        <span className={styles.retireChevron}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className={styles.sentinelaBody}>
          <ul className={styles.retireList}>
            {alarmes.map((a, i) => (
              <li key={`${a.tipo}-${a.slug}-${i}`} className={styles.retireRow}>
                <Badge tone={a.gravidade === 'alta' ? 'warn' : 'neutral'} size="xs">
                  {a.tipo}
                </Badge>
                <span className={styles.retireTema}>{a.detalhe}</span>
              </li>
            ))}
          </ul>
          <div className={styles.sentinelaFoot}>
            <span className={styles.muted}>
              {q.data?.em
                ? `verificado em ${formatarDataHora(q.data.em)}`
                : 'nunca verificado'}
            </span>
            <Button size="sm" variant="secondary" onClick={varrer} loading={varrendo}>
              Verificar agora
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── M2/M4: bandeira de "excluído no WordPress" + fila de aposentadoria ──────
// Matéria que o sistema publicou e que SAIU do ar no WP (lixeira/rascunho/
// apagado) aparece aqui cobrando um destino: URL do 301 ou "410" consciente.
// Apagar sem redirect foi o que fabricou 403 URLs mortas no cliente #1.

const ESTADO_WP_LABEL: Record<string, string> = {
  trash: 'na lixeira',
  draft: 'virou rascunho',
  private: 'privado',
  pending: 'pendente',
  apagado: 'APAGADO de vez',
};

function AposentadoriaBanner() {
  const [open, setOpen] = useState(false);
  const [destinos, setDestinos] = useState<Record<number, string>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['aposentadoria'],
    queryFn: () => boardApi.aposentadoria(),
    staleTime: 5 * 60_000,
  });
  const itens = q.data ?? [];
  const decidir = async (item: AposentadoriaItem) => {
    const destino = (destinos[item.pauta_id] || '').trim();
    if (!destino) return;
    try {
      await boardApi.aposentadoriaDecidir(item.pauta_id, destino);
      setFeedback(`Destino registrado para "${item.tema}". Aplicar o 301 no Rank Math.`);
      q.refetch();
    } catch (e) {
      setFeedback(`Erro ao registrar: ${String(e)}`);
    }
  };
  if (itens.length === 0) return null;
  return (
    <div className={styles.retireBanner}>
      <button className={styles.retireHead} onClick={() => setOpen((s) => !s)}>
        <span className={styles.retireFlag}></span>
        <strong>{itens.length}</strong>&nbsp;
        {itens.length === 1
          ? 'matéria publicada saiu do ar no WordPress sem redirect'
          : 'matérias publicadas saíram do ar no WordPress sem redirect'}
        &nbsp;— decidir destino
        <span className={styles.retireChevron}>{open ? '▲' : '▼'}</span>
      </button>
      {feedback && <p className={styles.retireFeedback}>{feedback}</p>}
      {open && (
        <ul className={styles.retireList}>
          {itens.map((it) => (
            <li key={it.pauta_id} className={styles.retireRow}>
              <div className={styles.retireInfo}>
                <span className={styles.retireTema}>{it.tema}</span>
                <Badge tone="warn" size="xs">
                  {ESTADO_WP_LABEL[it.estado_wp ?? ''] ?? it.estado_wp}
                </Badge>
                {it.wp_link && (
                  <a href={it.wp_link} target="_blank" rel="noreferrer" className={styles.retireLink}>
                    URL morta ↗
                  </a>
                )}
              </div>
              <div className={styles.retireAction}>
                <input
                  type="url"
                  placeholder="URL de destino do 301 (ou 410)"
                  value={destinos[it.pauta_id] ?? ''}
                  onChange={(e) =>
                    setDestinos((d) => ({ ...d, [it.pauta_id]: e.target.value }))
                  }
                  className={styles.retireInput}
                />
                <Button size="sm" onClick={() => decidir(it)}>
                  Registrar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrigemBadge({ origem }: { origem?: string }) {
  if (!origem) return null;
  // rótulos legados sistema/manual ainda aparecem; os ricos (ex.: 'Inteligência · auto') também
  const label = origem === 'sistema' ? 'Sistema' : origem === 'manual' ? 'Manual' : origem;
  const lo = origem.toLowerCase();
  const tone = lo.includes('manual') ? 'warn' : lo === 'sistema' || lo.includes('·') ? 'info' : 'neutral';
  return <Badge tone={tone} size="xs">{label}</Badge>;
}

function StatusBadge({ status }: { status?: string }) {
  // rascunho ganha tag; publicado aparece normal (sem badge), como as outras
  if (status === 'draft') return <Badge tone="warn" size="xs">rascunho</Badge>;
  if (status === 'trash') return <Badge tone="neutral" size="xs">lixeira</Badge>;
  return null;
}

function GncDeskBadge({ on }: { on?: boolean }) {
  if (on == null) return null;
  return on ? (
    <Badge tone="info" size="xs">Desk</Badge>
  ) : (
    <Badge tone="neutral" size="xs">Externo</Badge>
  );
}

function MetricsChips({ m }: { m?: ArticleMetricsLite | null }) {
  if (!m) return null;
  return (
    <div className={styles.metricsRow}>
      <span className={styles.metric} title="Pageviews (GA4, 28 dias)">{fInt(m.pageviews)}</span>
      <span
        className={styles.metric}
        title="CTR na busca (Search Console, 28d). '—' = sem impressões na busca (tráfego não veio do Google)."
      >
        CTR {m.impressions ? fPct(m.ctr) : '—'}
      </span>
      <span
        className={styles.metric}
        title="Posição média no Google (28d). '—' = sem impressões na busca."
      >
        pos {m.impressions ? fPos(m.position) : '—'}
      </span>
      <span className={styles.metric} title="Tempo médio de leitura">{fSecs(m.engagement_time)}</span>
    </div>
  );
}

function RankedList({
  rows,
  loading,
  site,
}: {
  rows?: RundownRow[];
  loading: boolean;
  site: string;
}) {
  if (loading) return <p className={styles.muted}>Carregando ranking…</p>;
  if (!rows || rows.length === 0) {
    return (
      <div className={styles.empty}>
        Sem dados de analytics para rankear nesta janela (28 dias). Os números aparecem conforme o
        sync do GA4/Search Console cobre as matérias.
      </div>
    );
  }
  return (
    <ol className={styles.ranked}>
      {rows.map((r, i) => (
        <li key={r.wp_post_id} className={styles.rankedRow}>
          <span className={styles.rankNum}>{i + 1}</span>
          {r.thumbnail ? (
            <img src={r.thumbnail} alt="" className={styles.thumb} loading="lazy" />
          ) : (
            <div className={styles.thumbPlaceholder}>sem imagem</div>
          )}
          <div className={styles.body}>
            <Link
              to={`/artigos/${r.wp_post_id}${site ? `?site=${site}` : ''}`}
              className={styles.rowLink}
            >
              {r.title ?? `#${r.wp_post_id}`}
            </Link>
            <div className={styles.meta}>
              <GncDeskBadge on={r.is_gnc_desk} />
              {r.editoria && <Badge tone="neutral" size="xs">{r.editoria}</Badge>}
            </div>
            <div className={styles.metricsRow}>
              <span className={styles.metric} title="Pageviews (28d)">{fInt(r.pageviews)}</span>
              <span className={styles.metric} title="CTR na busca (28d); '—' = sem impressões">CTR {r.gsc_impressions ? fPct(r.gsc_ctr) : '—'}</span>
              <span className={styles.metric} title="Cliques na busca (28d)">cliques {fInt(r.gsc_clicks)}</span>
              <span className={styles.metric} title="Posição média (28d); '—' = sem impressões">pos {r.gsc_impressions ? fPos(r.gsc_position) : '—'}</span>
              <span className={styles.metric} title="Tempo médio de leitura">{fSecs(r.avg_engagement_time)}</span>
            </div>
          </div>
          <div className={styles.rowActions}>
            <EditArticleButton id={Number(r.wp_post_id)} site={site} />
          </div>
        </li>
      ))}
    </ol>
  );
}
